// Four ways to get to one bit, chosen per content type.
//
// This is a per-content decision, not a global setting, because the four kinds
// of thing this app faxes fail in four different ways:
//
//   ATKINSON   photos. Diffuses only 6/8 of the error, so highlights blow and
//              shadows crush — which is exactly what a 1984 machine did, and it
//              keeps a face recognisable at this coarse a grid better than
//              Floyd-Steinberg does.
//   FS-SERP    the FINE setting. Best tonal fidelity. SERPENTINE IS NOT
//              OPTIONAL: plain left-to-right Floyd-Steinberg walks its error in
//              one direction and produces visible diagonal worming on skin and
//              skies. Alternating direction per row cancels it.
//   THRESHOLD  text, line art, a drawn cover sheet. These are already 1-bit.
//              Dithering black strokes only makes them fuzzy, and a real fax
//              thresholds them too.
//   BAYER8     UI screenshots. Flat blocks of colour become a regular halftone,
//              which is correct — it is a photocopy of a screen — and near-black
//              and near-white clamp hard, so text stays crisp.
//
// The working buffer is Int16Array, not Float32Array: diffused error routinely
// pushes a pixel outside 0..255, so Uint8 is wrong, but Int16 holds the range
// at half the memory of Float32. At 1080x714 that is 1.5 MB rather than 3 MB,
// which matters on the 2 GB phones this has to survive.

import { MID, PURE_BLACK, PURE_WHITE } from './geometry.js';

/** 8x8 Bayer, the standard recursive ordered matrix, normalised 0..63. */
const BAYER8 = Int16Array.from([
  0, 32, 8, 40, 2, 34, 10, 42,
  48, 16, 56, 24, 50, 18, 58, 26,
  12, 44, 4, 36, 14, 46, 6, 38,
  60, 28, 52, 20, 62, 30, 54, 22,
  3, 35, 11, 43, 1, 33, 9, 41,
  51, 19, 59, 27, 49, 17, 57, 25,
  15, 47, 7, 39, 13, 45, 5, 37,
  63, 31, 55, 23, 61, 29, 53, 21,
]);

const clamp8 = (v) => (v < 0 ? 0 : v > 255 ? 255 : v);

/**
 * Atkinson. Bill Atkinson's, from the original Macintosh.
 *
 * Only 6/8 of the error is passed on; the missing quarter is what blows the
 * highlights and crushes the shadows, and is the entire reason this looks like
 * a period machine rather than a modern halftone.
 */
export function atkinson(buf, w, h) {
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const i = y * w + x;
      const old = buf[i];
      const nu = old < MID ? PURE_BLACK : PURE_WHITE;
      buf[i] = nu;
      const err = (old - nu) >> 3; // one eighth, six times = 6/8
      if (x + 1 < w) buf[i + 1] += err;
      if (x + 2 < w) buf[i + 2] += err;
      if (y + 1 < h) {
        if (x > 0) buf[i + w - 1] += err;
        buf[i + w] += err;
        if (x + 1 < w) buf[i + w + 1] += err;
      }
      if (y + 2 < h) buf[i + w * 2] += err;
    }
  }
  return buf;
}

/** Floyd-Steinberg, serpentine. 7/16, 3/16, 5/16, 1/16, mirrored on odd rows. */
export function floydSteinberg(buf, w, h) {
  for (let y = 0; y < h; y += 1) {
    const ltr = (y & 1) === 0;
    const x0 = ltr ? 0 : w - 1;
    const x1 = ltr ? w : -1;
    const step = ltr ? 1 : -1;
    for (let x = x0; x !== x1; x += step) {
      const i = y * w + x;
      const old = buf[i];
      const nu = old < MID ? PURE_BLACK : PURE_WHITE;
      buf[i] = nu;
      const err = old - nu;
      const fwd = step;
      if (x + fwd >= 0 && x + fwd < w) buf[i + fwd] += (err * 7) >> 4;
      if (y + 1 < h) {
        if (x - fwd >= 0 && x - fwd < w) buf[i + w - fwd] += (err * 3) >> 4;
        buf[i + w] += (err * 5) >> 4;
        if (x + fwd >= 0 && x + fwd < w) buf[i + w + fwd] += (err * 1) >> 4;
      }
    }
  }
  return buf;
}

/** Hard threshold. For anything already 1-bit in spirit. */
export function threshold(buf, w, h, at = MID) {
  for (let i = 0; i < buf.length; i += 1) buf[i] = buf[i] < at ? PURE_BLACK : PURE_WHITE;
  return buf;
}

/** Ordered 8x8. Regular, patterned, and very compressible. */
export function bayer(buf, w, h) {
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const i = y * w + x;
      // Map the 0..63 matrix across the full range, centred on the threshold.
      const t = ((BAYER8[(y & 7) * 8 + (x & 7)] + 0.5) / 64) * 255;
      buf[i] = buf[i] < t ? PURE_BLACK : PURE_WHITE;
    }
  }
  return buf;
}

export const ALGORITHMS = { atkinson, floydSteinberg, threshold, bayer };

/** Which one a given kind of content should use. */
export function algorithmFor(kind, fine = false) {
  if (kind === 'memo') return threshold;
  if (kind === 'screenshot') return bayer;
  return fine ? floydSteinberg : atkinson;
}

/** Copy a greyscale byte array into the Int16 working buffer the ditherers want. */
export function toWorkBuffer(grey) {
  const buf = new Int16Array(grey.length);
  buf.set(grey);
  return buf;
}

/** Back to bytes, clamped. Every value is already 0 or 255 after a dither. */
export function toBytes(buf) {
  const out = new Uint8ClampedArray(buf.length);
  for (let i = 0; i < buf.length; i += 1) out[i] = clamp8(buf[i]);
  return out;
}
